import { Text } from '@airwallex/typography';
import React, { useCallback, useContext, useState } from 'react';
import { HStack, VStack } from '@airwallex/stacks';
import { Button } from '@airwallex/button';
import { IconChevronDown, IconChevronUp, IconCoinLarge, IconDocument, IconDownload } from '@airwallex/glyphs';
import { useTranslation } from 'react-i18next';
import { downloadFile } from '@/utils/downloadFile';
import { useMobile } from '@/hooks/useMediaQuery';
import { StoreContext } from '@/providers/storeProvider';
import { airTracker } from '@/utils/airTracker';
import { COMPONENT_NAME } from '@/pages/Main';
import { DropdownButton, DropdownItem } from '@airwallex/dropdown';
import { Dialog } from '@airwallex/dialog';
import { styled, useTheme } from '@airwallex/theme';
import { css } from '@emotion/react';

const Separator = styled.div(({ theme }) => ({
  width: '100%',
  color: theme.colors.grays[30],
  fontWeight: 700,
  borderBottom: `1px solid ${theme.colors.grays[30]}`,
}));

const DownloadButton: React.FC = () => {
  const { t } = useTranslation();
  const { invoiceData } = useContext(StoreContext);
  const { invoice, creditNotes } = invoiceData;
  const { pdfUrl } = invoice;
  const isMobile = useMobile();
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);
  const { semanticColors } = useTheme();

  const downloadPdf = useCallback((pdfUrl: string | undefined) => {
    if (pdfUrl) {
      airTracker.info('downloadPdf', { component: COMPONENT_NAME, pdfUrl });
      downloadFile(pdfUrl);
    }
  }, []);

  const downloadAllPdf = useCallback(async () => {
    const pdfUrls = [pdfUrl, ...(creditNotes?.map((creditNote) => creditNote.pdfUrl) ?? [])];
    for (const pdfUrl of pdfUrls) {
      if (pdfUrl) {
        airTracker.info('downloadInvoice', { component: COMPONENT_NAME, pdfUrl });
        downloadFile(pdfUrl);
        await new Promise((r) => setTimeout(r, 1000));
      }
    }
  }, [creditNotes, pdfUrl]);

  if (!pdfUrl) {
    return null;
  }

  if (!creditNotes || creditNotes.length === 0) {
    return (
      <Button variant="SECONDARY" iconBefore={IconDownload} onClick={() => downloadPdf(pdfUrl)}>
        {t('invoice')}
      </Button>
    );
  }

  if (!isMobile) {
    return (
      <DropdownButton
        icon={IconDownload}
        buttonText={t('Download')}
        style={{ border: `1px solid ${semanticColors.border.decorative}` }}
        menuRight={true}
      >
        <DropdownItem onClick={() => downloadAllPdf()}>{t('pdf.allFiles')}</DropdownItem>
        <Separator />
        <DropdownItem icon={IconDocument} onClick={() => downloadPdf(pdfUrl)}>
          <HStack space="8px" align="CENTER">
            <IconDocument color={semanticColors.icon.secondary} size={16} style={{ margin: '3px 2px' }} />
            <VStack align="STRETCH">
              <Text variant="body" style={{ textAlign: 'left' }}>
                {t('invoice')}
              </Text>
              <Text variant="hint" color="secondary">
                {invoice.number}
              </Text>
            </VStack>
          </HStack>
        </DropdownItem>
        {creditNotes?.map((creditNote) => (
          <DropdownItem key={creditNote.id} onClick={() => downloadPdf(creditNote.pdfUrl)}>
            <HStack space="8px" align="CENTER">
              <IconCoinLarge color={semanticColors.icon.secondary} size={16} style={{ margin: '3px 2px' }} />
              <VStack align="STRETCH">
                <Text variant="body" style={{ textAlign: 'left' }}>
                  {t('creditNote.title')}
                </Text>
                <Text variant="hint" color="secondary">
                  {creditNote.number}
                </Text>
              </VStack>
            </HStack>
          </DropdownItem>
        ))}
      </DropdownButton>
    );
  } else {
    return (
      <>
        <Button
          variant="SECONDARY"
          iconBefore={IconDownload}
          iconAfter={isDownloadOpen ? IconChevronUp : IconChevronDown}
          onClick={() => setIsDownloadOpen(!isDownloadOpen)}
        >
          {t('Download')}
        </Button>
        <Dialog
          width={'medium'}
          stopPropagation={true}
          isDismissible={true}
          open={isDownloadOpen}
          onClose={() => setIsDownloadOpen(!isDownloadOpen)}
          contentStyles={css({
            '> article': {
              marginBlock: '0',
            },
            '> button': {
              display: 'none',
            },
          })}
        >
          <VStack align="LEFT" space="16px">
            <Text
              variant="headline400"
              onClick={() => downloadAllPdf()}
              css={{
                padding: '8px 0',
                marginBottom: '0',
                width: '100%',
              }}
            >
              {t('pdf.allFiles')}
            </Text>
            <Separator
              style={{
                marginTop: '8px',
              }}
            />

            <HStack space="8px" align="CENTER" onClick={() => downloadPdf(pdfUrl)}>
              <IconDocument color={semanticColors.icon.secondary} size={16} style={{ margin: '3px 2px' }} />
              <VStack align="STRETCH">
                <Text variant="body" style={{ textAlign: 'left' }}>
                  {t('invoice')}
                </Text>
                <Text variant="hint" color="secondary">
                  {invoice.number}
                </Text>
              </VStack>
            </HStack>
            {creditNotes?.map((creditNote) => (
              <HStack key={creditNote.id} space="8px" align="CENTER" onClick={() => downloadPdf(creditNote.pdfUrl)}>
                <IconCoinLarge color={semanticColors.icon.secondary} size={16} style={{ margin: '3px 2px' }} />
                <VStack align="STRETCH">
                  <Text variant="body" style={{ textAlign: 'left' }}>
                    {t('creditNote.title')}
                  </Text>
                  <Text variant="hint" color="secondary">
                    {creditNote.number}
                  </Text>
                </VStack>
              </HStack>
            ))}
          </VStack>
        </Dialog>
      </>
    );
  }
};

export default DownloadButton;
